import React, { Component } from 'react';
import CustomSelect from './CustomSelect';
import { DropdownChangeParams } from 'primereact/dropdown';
import Icon from '../../icons/Icon';
import { DataTableSortMeta } from 'primereact/datatable';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from './FormicaSearchSelectBox';

export interface CustomPaginatorProps {
  rows: number;
  page: number;
  pageCount: number;
  sort?: DataTableSortMeta[];
}

class CustomPaginator extends Component<Props, State> {
  public state = new State();

  public render() {
    const { rowsPerPageOptions, rows, totalRecords, currentPage, onPageChange, fixed, borderHidden, loading } = this.props;
    const { totalPages } = this.state;

    const renderPages = [];

    for (let i = currentPage - 4; i <= currentPage + 4; i++) {
      if (i > 0 && i <= totalPages) {
        renderPages.push(
          <div
            key={i}
            data-testid={`pagination-page-${i}`}
            onClick={() => !loading && onPageChange({ rows, page: i, pageCount: totalPages })}
            className={`w-full min-w-[24px] px-3 py-1   h-[22px] hover:cursor-pointer flex items-center justify-center shadow rounded ${
              currentPage === i ? 'bg-primary text-white' : 'bg-[#F6F6F9] text-primary'
            } ${loading ? 'user-not-allowed' : ''}`}
          >
            {i}
          </div>,
        );
      }
    }

    return (
      totalRecords > 0 &&
      totalRecords !== undefined && (
        <div className={`w-full ${fixed ? 'mt-2' : 'fixed'} bottom-0.5 z-9 bg-white`}>
          <div
            className={`${fixed ? 'border' : ' border-t-[1px] border-b-[1px] '} ${borderHidden ? 'border-none' : ''} flex w-full font-formica items-center justify-center relative py-4`}
          >
            {rowsPerPageOptions.length > 0 && (
              <div className={'w-13 group absolute left-0 ml-2'}>
                <CustomSelect
                  disabled={loading}
                  onChange={(e: DropdownChangeParams) => {
                    const { onPageChange } = this.props;
                    onPageChange({ rows: e.value as number, page: 1, pageCount: totalPages });
                  }}
                  dropdownIcon={
                    'pi pi-chevron-down text-f-black-4 group-hover:text-f-purple group-focus:hover:text-f-purple text-xs flex items-center justify-start'
                  }
                  value={rows}
                  options={rowsPerPageOptions.map((row) => ({ label: String(row), value: row }))}
                />
              </div>
            )}
            <div className={`flex gap-x-4 items-center justify-center ${fixed ? '' : 'mr-32'}`}>
              <button data-testid='pagination-start-button' className={`cursor-pointer`} onClick={this.handleStartClick}>
                <Icon icon={'expression_icon_lessthanorequals'} size={16} />
              </button>
              <button
                data-testid='pagination-previous-button'
                className={`${this.canGoPrevious() ? 'hover:cursor-pointer' : ''}`}
                onClick={this.handlePrevClick}
              >
                <Icon icon={'arrow-left'} size={16} />
              </button>
              <div className={'flex gap-x-3 items-center justify-center'}>{renderPages}</div>
              <button data-testid='pagination-next-button' className={`${this.canGoNext() ? 'hover:cursor-pointer' : ''}`} onClick={this.handleNextClick}>
                <Icon icon={'arrow-right'} size={16} />
              </button>
              <button data-testid='pagination-end-button' className={`cursor-pointer`} onClick={this.handleEndClick}>
                <Icon icon={'expression_icon_greaterthanorequals-1'} size={16} />
              </button>
              <div className={'flex w-[60px] max-h-[20px] items-center justify-start h-full'}>
                <FormicaSearchSelectBox
                  options={this.getTotalPagesOptions()}
                  rounded
                  value={this.props.currentPage}
                  onChange={(item: FormicaSelectBoxItem) => {
                    this.handleCustomPageClick(item.value as number);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )
    );
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const { rows, totalRecords, currentPage } = this.props;
    if (prevProps.currentPage !== currentPage) {
      this.setState({ totalPages: Math.ceil(totalRecords / rows) });
    }
    if (prevProps.rows !== rows || prevProps.totalRecords !== totalRecords) {
      const totalPages = Math.ceil(totalRecords / rows);
      this.setState({ totalPages });
    }
    if (prevProps.currentRecords != 0 && this.props.currentRecords == 0 && currentPage > 1) {
      this.props.onPageChange({ rows: rows, page: currentPage - 1, pageCount: this.state.totalPages });
    }
  }

  public componentDidMount() {
    const { rows, totalRecords, currentRecords, currentPage } = this.props;
    if (currentRecords == 0 && currentPage > 1) {
      this.props.onPageChange({ rows: rows, page: currentPage - 1, pageCount: this.state.totalPages });
    }
    this.setState({ totalPages: Math.ceil(totalRecords / rows) });
  }

  private getTotalPagesOptions = (): FormicaSelectBoxItem[] => {
    const { totalPages } = this.state;
    const { currentPage } = this.props;
    const options = [];
    for (let i = 1; i <= totalPages; i++) {
      if (currentPage - 200 < i && i < currentPage + 200) options.push({ label: i.toString(), value: i });
    }
    return options;
  };

  private handleNextClick = () => {
    const { currentPage, onPageChange, rows } = this.props;
    const { totalPages } = this.state;
    if (this.canGoNext()) {
      onPageChange({ rows: rows, page: currentPage + 1, pageCount: totalPages });
    }
  };

  private handleCustomPageClick = (page: number) => {
    const { onPageChange, rows } = this.props;
    const { totalPages } = this.state;
    onPageChange({ rows: rows, page: page, pageCount: totalPages });
  };

  private handlePrevClick = () => {
    const { currentPage, onPageChange, rows } = this.props;
    const { totalPages } = this.state;
    if (this.canGoPrevious()) {
      onPageChange({ rows: rows, page: currentPage - 1, pageCount: totalPages });
    }
  };

  private handleStartClick = () => {
    const { onPageChange, rows } = this.props;
    const { totalPages } = this.state;
    onPageChange({ rows: rows, page: 1, pageCount: totalPages });
  };

  private handleEndClick = () => {
    const { onPageChange, rows } = this.props;
    const { totalPages } = this.state;
    onPageChange({ rows: rows, page: totalPages, pageCount: totalPages });
  };

  private canGoNext = () => {
    const { totalPages } = this.state;
    const { currentPage, loading } = this.props;
    return currentPage <= totalPages - 1 && !loading;
  };

  private canGoPrevious = () => {
    const { currentPage, loading } = this.props;
    return currentPage > 1 && !loading;
  };
}

interface Props {
  rows: number;
  totalRecords: number;
  currentRecords?: number;
  rowsPerPageOptions: number[];
  onPageChange: (event: CustomPaginatorProps) => void;
  currentPage: number;
  fixed?: boolean;
  borderHidden?: boolean;
  loading?: boolean;
}

class State {
  totalPages = 0;
}

export default CustomPaginator;
